import {
  createDefaultFlexChildLayout,
  createDefaultFrameFlexLayout,
  createDefaultFrameMirrorLayout,
  createDefaultFrameRepeatLayout,
  flexChildLayout,
  frameFlexLayout,
  frameMirrorLayout,
  frameRepeatLayout,
  type AutoLayoutMode,
  type FlexChildLayout,
  type FlexLayoutAlign,
  type FlexLayoutDirection,
  type FlexLayoutJustify,
  type FlexAxisSizing,
  type FrameFlexLayout,
  type FrameMirrorLayout,
  type FrameRepeatLayout,
  type FrameNode,
  type LayoutPadding,
  type MirrorLayoutAxis,
  type RepeatLayoutMode,
  type SceneNode,
} from "@/document/model/nodes";
import { Metric } from "./Metric";
import { PanelLabeledSelect } from "./PanelSelect";
import { PanelSection } from "./PanelSection";

type FrameLayoutPatch = {
  autoLayout?: AutoLayoutMode;
  flexLayout?: FrameFlexLayout;
  repeatLayout?: FrameRepeatLayout;
  mirrorLayout?: FrameMirrorLayout;
};

interface LayoutSectionProps {
  selectedNode: SceneNode;
  parentFlexFrame?: FrameNode;
  onFrameLayoutChange: (frameId: string, patch: FrameLayoutPatch) => void;
  onFlexChildLayoutChange: (nodeId: string, layout: FlexChildLayout) => void;
}

const autoLayoutModes: Array<{ value: AutoLayoutMode; label: string }> = [
  { value: "off", label: "Off" },
  { value: "flex", label: "Flex" },
  { value: "repeat", label: "Repeat" },
  { value: "mirror", label: "Mirror" },
];

const directions: Array<{ value: FlexLayoutDirection; label: string }> = [
  { value: "row", label: "Row" },
  { value: "column", label: "Column" },
  { value: "row-reverse", label: "Row reverse" },
  { value: "column-reverse", label: "Column reverse" },
];

const justifyOptions: Array<{ value: FlexLayoutJustify; label: string }> = [
  { value: "flex-start", label: "Start" },
  { value: "center", label: "Center" },
  { value: "flex-end", label: "End" },
  { value: "space-between", label: "Space between" },
  { value: "space-around", label: "Space around" },
  { value: "space-evenly", label: "Space evenly" },
];

const alignOptions: Array<{ value: Exclude<FlexLayoutAlign, "auto">; label: string }> = [
  { value: "flex-start", label: "Start" },
  { value: "center", label: "Center" },
  { value: "flex-end", label: "End" },
  { value: "stretch", label: "Stretch" },
];

const alignSelfOptions: Array<{ value: FlexLayoutAlign; label: string }> = [
  { value: "auto", label: "Auto" },
  ...alignOptions,
];

const axisSizingOptions: Array<{ value: FlexAxisSizing; label: string }> = [
  { value: "hug", label: "Hug" },
  { value: "fixed", label: "Fixed" },
];

const repeatModes: Array<{ value: RepeatLayoutMode; label: string }> = [
  { value: "row", label: "Row" },
  { value: "column", label: "Column" },
  { value: "grid", label: "Grid" },
  { value: "radial", label: "Radial" },
];

const mirrorAxes: Array<{ value: MirrorLayoutAxis; label: string }> = [
  { value: "horizontal", label: "Horizontal" },
  { value: "vertical", label: "Vertical" },
  { value: "both", label: "Both" },
];

export function LayoutSection({ selectedNode, parentFlexFrame, onFrameLayoutChange, onFlexChildLayoutChange }: LayoutSectionProps) {
  const selectedFrame = selectedNode.type === "frame" ? selectedNode : undefined;
  const flexLayout = selectedFrame ? frameFlexLayout(selectedFrame) : createDefaultFrameFlexLayout();
  const repeatLayout = selectedFrame ? frameRepeatLayout(selectedFrame) : createDefaultFrameRepeatLayout();
  const mirrorLayout = selectedFrame ? frameMirrorLayout(selectedFrame) : createDefaultFrameMirrorLayout();
  const childLayout = flexChildLayout(selectedNode);
  const mode = selectedFrame?.autoLayout ?? "off";

  if (!selectedFrame && !parentFlexFrame) {
    return null;
  }

  return (
    <PanelSection title="Layout">
      <div className="grid gap-2">
        {selectedFrame ? (
          <>
            <PanelLabeledSelect
              label="Mode"
              value={mode}
              options={autoLayoutModes}
              onChange={value => {
                const nextMode = value as AutoLayoutMode;
                onFrameLayoutChange(selectedFrame.id, {
                  autoLayout: nextMode,
                  flexLayout: selectedFrame.flexLayout ?? createDefaultFrameFlexLayout(),
                  repeatLayout: selectedFrame.repeatLayout ?? createDefaultFrameRepeatLayout(),
                  mirrorLayout: selectedFrame.mirrorLayout ?? createDefaultFrameMirrorLayout(),
                });
              }}
            />
            {mode === "flex" ? (
              <FrameFlexControls
                layout={flexLayout}
                onChange={next => onFrameLayoutChange(selectedFrame.id, { autoLayout: "flex", flexLayout: next })}
              />
            ) : null}
            {mode === "repeat" ? (
              <FrameRepeatControls
                layout={repeatLayout}
                onChange={next => onFrameLayoutChange(selectedFrame.id, { autoLayout: "repeat", repeatLayout: next })}
              />
            ) : null}
            {mode === "mirror" ? (
              <FrameMirrorControls
                layout={mirrorLayout}
                onChange={next => onFrameLayoutChange(selectedFrame.id, { autoLayout: "mirror", mirrorLayout: next })}
              />
            ) : null}
          </>
        ) : null}

        {parentFlexFrame ? (
          <FlexChildControls
            layout={childLayout}
            onChange={next => onFlexChildLayoutChange(selectedNode.id, next)}
          />
        ) : null}
      </div>
    </PanelSection>
  );
}

function FrameFlexControls({ layout, onChange }: { layout: FrameFlexLayout; onChange: (layout: FrameFlexLayout) => void }) {
  return (
    <>
      <PanelLabeledSelect
        label="Direction"
        value={layout.direction}
        options={directions}
        onChange={value => onChange({ ...layout, direction: value as FlexLayoutDirection })}
      />
      <div className="grid grid-cols-2 gap-2">
        <PanelLabeledSelect
          label="Width"
          value={layout.widthSizing ?? "hug"}
          options={axisSizingOptions}
          onChange={value => onChange({ ...layout, widthSizing: value as FlexAxisSizing })}
        />
        <PanelLabeledSelect
          label="Height"
          value={layout.heightSizing ?? "hug"}
          options={axisSizingOptions}
          onChange={value => onChange({ ...layout, heightSizing: value as FlexAxisSizing })}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <PanelLabeledSelect
          label="Justify"
          value={layout.justifyContent}
          options={justifyOptions}
          onChange={value => onChange({ ...layout, justifyContent: value as FlexLayoutJustify })}
        />
        <PanelLabeledSelect
          label="Align"
          value={layout.alignItems}
          options={alignOptions}
          onChange={value => onChange({ ...layout, alignItems: value as Exclude<FlexLayoutAlign, "auto"> })}
        />
      </div>
      <Metric
        label="Gap"
        value={layout.gap}
        disabled={false}
        min={0}
        onCommit={gap => onChange({ ...layout, gap })}
      />
      <Metric
        label="P"
        value={sharedPaddingValue(layout.padding)}
        disabled={false}
        min={0}
        onCommit={padding => onChange({ ...layout, padding: { top: padding, right: padding, bottom: padding, left: padding } })}
      />
      <div className="grid grid-cols-2 gap-2">
        <Metric label="PT" value={layout.padding.top} disabled={false} min={0} onCommit={top => onChange({ ...layout, padding: { ...layout.padding, top } })} />
        <Metric label="PR" value={layout.padding.right} disabled={false} min={0} onCommit={right => onChange({ ...layout, padding: { ...layout.padding, right } })} />
        <Metric label="PB" value={layout.padding.bottom} disabled={false} min={0} onCommit={bottom => onChange({ ...layout, padding: { ...layout.padding, bottom } })} />
        <Metric label="PL" value={layout.padding.left} disabled={false} min={0} onCommit={left => onChange({ ...layout, padding: { ...layout.padding, left } })} />
      </div>
    </>
  );
}

function FrameRepeatControls({ layout, onChange }: { layout: FrameRepeatLayout; onChange: (layout: FrameRepeatLayout) => void }) {
  return (
    <>
      <PanelLabeledSelect
        label="Pattern"
        value={layout.mode}
        options={repeatModes}
        onChange={value => {
          const mode = value as RepeatLayoutMode;
          if (mode !== "grid") {
            onChange({ ...layout, mode });
            return;
          }
          // Materialize an explicit grid so Cols×Rows matches visible copies.
          const columns = Math.max(1, layout.columns);
          const rows = layout.rows > 0 ? layout.rows : Math.max(1, Math.ceil(layout.count / columns));
          onChange({ ...layout, mode, columns, rows, count: columns * rows });
        }}
      />
      {layout.mode === "grid" ? (
        <div className="grid grid-cols-2 gap-2">
          <Metric
            label="Cols"
            value={layout.columns}
            disabled={false}
            min={1}
            step={1}
            onCommit={columns => {
              const nextColumns = Math.max(1, Math.round(columns));
              const rows = Math.max(1, layout.rows > 0 ? layout.rows : Math.ceil(layout.count / nextColumns));
              onChange({ ...layout, columns: nextColumns, rows, count: nextColumns * rows });
            }}
          />
          <Metric
            label="Rows"
            value={layout.rows > 0 ? layout.rows : Math.max(1, Math.ceil(layout.count / Math.max(1, layout.columns)))}
            disabled={false}
            min={1}
            step={1}
            onCommit={rows => {
              const nextRows = Math.max(1, Math.round(rows));
              const columns = Math.max(1, layout.columns);
              onChange({ ...layout, columns, rows: nextRows, count: columns * nextRows });
            }}
          />
        </div>
      ) : (
        <Metric
          label="Count"
          value={layout.count}
          disabled={false}
          min={1}
          step={1}
          onCommit={count => onChange({ ...layout, count: Math.max(1, Math.round(count)) })}
        />
      )}
      {layout.mode === "grid" ? (
        <div className="grid grid-cols-2 gap-2">
          <Metric label="Col gap" value={layout.columnGap} disabled={false} min={0} onCommit={columnGap => onChange({ ...layout, columnGap })} />
          <Metric label="Row gap" value={layout.rowGap} disabled={false} min={0} onCommit={rowGap => onChange({ ...layout, rowGap })} />
        </div>
      ) : layout.mode === "radial" ? (
        <>
          <Metric label="Radius" value={layout.radius} disabled={false} min={0} onCommit={radius => onChange({ ...layout, radius })} />
          <Metric label="Angle" value={layout.startAngle} disabled={false} onCommit={startAngle => onChange({ ...layout, startAngle })} />
        </>
      ) : (
        <Metric label="Gap" value={layout.gap} disabled={false} min={0} onCommit={gap => onChange({ ...layout, gap })} />
      )}
    </>
  );
}

function FrameMirrorControls({ layout, onChange }: { layout: FrameMirrorLayout; onChange: (layout: FrameMirrorLayout) => void }) {
  return (
    <>
      <PanelLabeledSelect
        label="Axis"
        value={layout.axis}
        options={mirrorAxes}
        onChange={value => onChange({ ...layout, axis: value as MirrorLayoutAxis })}
      />
      <Metric
        label="Gap"
        value={layout.gap}
        disabled={false}
        min={0}
        onCommit={gap => onChange({ ...layout, gap })}
      />
    </>
  );
}

function sharedPaddingValue(padding: LayoutPadding): number | undefined {
  return padding.top === padding.right && padding.top === padding.bottom && padding.top === padding.left
    ? padding.top
    : undefined;
}

function FlexChildControls({ layout, onChange }: { layout: FlexChildLayout; onChange: (layout: FlexChildLayout) => void }) {
  const nextLayout = { ...createDefaultFlexChildLayout(), ...layout };
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <Metric label="Grow" value={nextLayout.grow} disabled={false} min={0} step={0.1} onCommit={grow => onChange({ ...nextLayout, grow })} />
        <Metric label="Shrink" value={nextLayout.shrink} disabled={false} min={0} step={0.1} onCommit={shrink => onChange({ ...nextLayout, shrink })} />
      </div>
      <PanelLabeledSelect
        label="Self"
        value={nextLayout.alignSelf}
        options={alignSelfOptions}
        onChange={value => onChange({ ...nextLayout, alignSelf: value as FlexLayoutAlign })}
      />
      <label className="flex h-8 items-center justify-between rounded bg-white/[0.04] px-2 text-[12px] text-slate-300">
        <span>Absolute</span>
        <input
          type="checkbox"
          checked={nextLayout.absolute}
          onChange={event => onChange({ ...nextLayout, absolute: event.currentTarget.checked })}
          className="h-3.5 w-3.5 accent-sky-500"
        />
      </label>
    </>
  );
}
